"use client";

import { useI18n } from "@/i18n/I18nProvider";
import { InfoPopover } from "../InfoPopover";

/**
 * The mail setup explained (the ⓘ beside the setup window's heading): why the mails go through a sending service on
 * port 2525 (the server's provider blocks the usual mail ports), then step by step the free way with Brevo — the
 * account, the domain and its DNS records, the sender, the SMTP key, what goes into the window, the test — and what
 * the usual errors mean. `domain`: the platform's address, named in the records.
 */

/** The steps, each its heading and its lines. */
const STEPS: { key: string; lines: number }[] = [
  { key: "account", lines: 1 },
  { key: "domain", lines: 3 },
  { key: "sender", lines: 1 },
  { key: "key", lines: 2 },
  { key: "test", lines: 1 },
];
/** The DNS records Brevo asks for, by their host name at the domain provider. */
const RECORDS = [
  { host: "@", type: "TXT", value: "brevo-code:…" },
  { host: "brevo1._domainkey", type: "CNAME", value: "b1.….dkim.brevo.com" },
  { host: "brevo2._domainkey", type: "CNAME", value: "b2.….dkim.brevo.com" },
  { host: "_dmarc", type: "TXT", value: "v=DMARC1; …" },
];
/** What goes into the setup window. */
const FIELDS = ["host", "securityLabel", "port", "user", "password", "sender", "senderName"];
const ERRORS = ["version", "refused", "timeout"];

const TABLE = "w-full border-collapse text-[12px] [&_td]:border-t [&_td]:border-line [&_td]:py-1 [&_td]:pr-3 [&_td]:align-top [&_th]:pb-1 [&_th]:pr-3 [&_th]:text-left [&_th]:font-medium [&_th]:text-muted";

export function MailGuide({ domain }: { domain: string }) {
  const { t } = useI18n();
  const g = (k: string) => t(`set.mail.guide.${k}`, { domain });
  return (
    <InfoPopover label={g("label")} title={g("title")}>
      <div className="space-y-3 leading-relaxed">
        <p className="text-ink-2">{g("intro")}</p>
        <ol className="list-decimal space-y-2.5 pl-5">
          {STEPS.map((s) => (
            <li key={s.key}>
              <span className="font-medium">{g(`${s.key}.title`)}</span>
              {Array.from({ length: s.lines }, (_, k) => <p key={k} className="text-ink-2">{g(`${s.key}.${k + 1}`)}</p>)}
              {s.key === "domain" && (
                <table className={`${TABLE} mt-1.5 font-mono break-all`}>
                  <thead><tr><th>{g("col.host")}</th><th>{g("col.type")}</th><th>{g("col.value")}</th></tr></thead>
                  <tbody>{RECORDS.map((r) => <tr key={r.host}><td>{r.host}</td><td>{r.type}</td><td>{r.value}</td></tr>)}</tbody>
                </table>
              )}
              {s.key === "key" && (
                <table className={`${TABLE} mt-1.5 break-words`}>
                  <tbody>{FIELDS.map((f) => <tr key={f}><td className="whitespace-nowrap">{t(`set.mail.${f}`)}</td><td>{g(`field.${f}`)}</td></tr>)}</tbody>
                </table>
              )}
            </li>
          ))}
        </ol>
        <div className="border-t border-line pt-2">
          <p className="font-medium">{g("errors")}</p>
          <ul className="list-disc space-y-1 pl-5 text-ink-2">{ERRORS.map((e) => <li key={e}>{g(`error.${e}`)}</li>)}</ul>
        </div>
      </div>
    </InfoPopover>
  );
}
